// ============================================================================
// CASE CLOSURE + HOLD SERVICE — Phase 7
// ============================================================================
// Wraps the /api/v1/CaseClosure and /api/v1/CaseHold endpoints. Closure and
// Hold are case-level actions (distinct from workflow transitions).

import { get, post } from '../client/axiosClient';
import {
  caseClosureEndpoints,
  caseHoldEndpoints,
} from '../endpoints/caseClosure.endpoints';
import type { ApiResponse, ID } from '../types';
import type {
  CaseClosure,
  CloseCaseRequest,
  ClosureReasonOption,
  ReopenCaseRequest,
} from '../types/caseClosure.types';
import type {
  CaseHold,
  CaseHoldHistoryEntry,
  HoldCaseRequest,
  HoldReasonOption,
  ResumeCaseRequest,
} from '../types/caseHold.types';

class CaseClosureService {
  async getActive(caseId: ID): Promise<CaseClosure | null> {
    const res = await get<ApiResponse<CaseClosure | null>>(caseClosureEndpoints.active(caseId));
    return res.data?.data ?? null;
  }

  async getHistory(caseId: ID): Promise<CaseClosure[]> {
    const res = await get<ApiResponse<CaseClosure[]>>(caseClosureEndpoints.history(caseId));
    return res.data?.data ?? [];
  }

  async getReasonOptions(caseId: ID): Promise<ClosureReasonOption[]> {
    const res = await get<ApiResponse<ClosureReasonOption[]>>(caseClosureEndpoints.reasonOptions(caseId));
    return res.data?.data ?? [];
  }

  async close(caseId: ID, body: CloseCaseRequest): Promise<CaseClosure> {
    const res = await post<ApiResponse<CaseClosure>>(caseClosureEndpoints.close(caseId), body);
    return res.data.data!;
  }

  async reopen(caseId: ID, body: ReopenCaseRequest): Promise<CaseClosure> {
    const res = await post<ApiResponse<CaseClosure>>(caseClosureEndpoints.reopen(caseId), body);
    return res.data.data!;
  }
}

class CaseHoldService {
  async getCurrent(caseId: ID): Promise<CaseHold | null> {
    const res = await get<ApiResponse<CaseHold | null>>(caseHoldEndpoints.current(caseId));
    return res.data?.data ?? null;
  }

  async getHistory(caseId: ID): Promise<CaseHoldHistoryEntry[]> {
    const res = await get<ApiResponse<CaseHoldHistoryEntry[]>>(caseHoldEndpoints.history(caseId));
    return res.data?.data ?? [];
  }

  async getReasonOptions(caseId: ID): Promise<HoldReasonOption[]> {
    const res = await get<ApiResponse<HoldReasonOption[]>>(caseHoldEndpoints.reasonOptions(caseId));
    return res.data?.data ?? [];
  }

  async hold(caseId: ID, body: HoldCaseRequest): Promise<CaseHold> {
    const res = await post<ApiResponse<CaseHold>>(caseHoldEndpoints.hold(caseId), body);
    return res.data.data!;
  }

  async resume(caseId: ID, body: ResumeCaseRequest): Promise<CaseHold> {
    const res = await post<ApiResponse<CaseHold>>(caseHoldEndpoints.resume(caseId), body);
    return res.data.data!;
  }
}

export const caseClosureService = new CaseClosureService();
export const caseHoldService = new CaseHoldService();
